/**
 * Utility to validate and log missing autocomplete attributes on input elements
 */
export function validateInputAutocomplete() {
  if (typeof window === 'undefined') return;

  // List of input types that should have autocomplete attributes
  const inputTypesRequiringAutocomplete = {
    email: 'email',
    password: 'current-password',
    tel: 'tel',
    text: ['given-name', 'family-name', 'name'],
  };

  // Find all input elements
  const inputs = document.querySelectorAll('input[type="email"], input[type="password"], input[type="tel"]');

  inputs.forEach((input) => {
    const type = input.getAttribute('type');
    const id = input.getAttribute('id') || 'no-id';
    const hasAutocomplete = input.hasAttribute('autocomplete');

    if (!hasAutocomplete) {
      const expectedAutocomplete = inputTypesRequiringAutocomplete[type];
      console.error(
        `⚠️ [ACCESSIBILITY] Input element missing autocomplete attribute`,
        {
          element: input,
          id,
          type,
          suggestedAutocomplete: expectedAutocomplete,
          html: input.outerHTML,
        }
      );

      // Also log in a format that's easy to spot
      console.warn(
        `🔒 FIX NEEDED: <input type="${type}" id="${id}"> is missing autocomplete="${expectedAutocomplete}"`
      );
    }
  });

  // Run periodic checks
  const checkInterval = setInterval(() => {
    const currentInputs = document.querySelectorAll('input[type="email"], input[type="password"], input[type="tel"]');
    let foundIssues = false;

    currentInputs.forEach((input) => {
      const type = input.getAttribute('type');
      const id = input.getAttribute('id') || 'no-id';
      const hasAutocomplete = input.hasAttribute('autocomplete');

      if (!hasAutocomplete) {
        foundIssues = true;
      }
    });

    if (foundIssues) {
      console.info('⏰ [Periodic Check] Still found inputs missing autocomplete attributes');
    }

    // Clear interval after 10 checks (10 minutes if checking every 60 seconds)
    if (currentInputs.length === 0) {
      clearInterval(checkInterval);
    }
  }, 60000); // Check every minute

  return {
    stop: () => clearInterval(checkInterval),
  };
}

/**
 * Monitor DOM for dynamically added inputs without autocomplete
 */
export function monitorDOMForMissingAutocomplete() {
  if (typeof window === 'undefined') return;

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if the node itself is an input
            if (node.tagName === 'INPUT') {
              const type = node.getAttribute('type');
              if (['email', 'password', 'tel'].includes(type)) {
                const hasAutocomplete = node.hasAttribute('autocomplete');
                if (!hasAutocomplete) {
                  const id = node.getAttribute('id') || 'no-id';
                  console.error(
                    `🔴 [NEW ELEMENT] Dynamically added input without autocomplete`,
                    {
                      type,
                      id,
                      element: node,
                    }
                  );
                }
              }
            }

            // Check children for inputs
            const childInputs = node.querySelectorAll ? node.querySelectorAll('input[type="email"], input[type="password"], input[type="tel"]') : [];
            childInputs.forEach((input) => {
              const type = input.getAttribute('type');
              const hasAutocomplete = input.hasAttribute('autocomplete');
              if (!hasAutocomplete) {
                const id = input.getAttribute('id') || 'no-id';
                console.error(
                  `🔴 [NEW ELEMENT] Dynamically added input without autocomplete`,
                  {
                    type,
                    id,
                    element: input,
                  }
                );
              }
            });
          }
        });
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  return {
    stop: () => observer.disconnect(),
  };
}
